import { ChangeEvent, useState } from 'react'
import CustomPaper from '../../containers/Paper'
import { Divider } from '@mui/material'
import CustomInput from '../../components/TextInput'
import CustomButton from '../../components/Button'
import { useNavigate } from 'react-router-dom'
import { AppRoutes } from '../../helpers/routes'
import {
    containsLowercase,
    containsNotBlankSpace,
    containsNumber,
    containsSpecialCharacter,
    containsUppercase,
    validatePassword,
} from '../../helpers/utils'
import { useSnackbar } from 'notistack'
import RecoverPasswordService from '../../services/RecoverPasswordService'
import useRecoverPassword from '../../hooks/useRecoverPassword'
import { Tooltip } from 'react-tooltip'
import { CheckRules, DEFAULT_CHECKS, PasswordTooltip } from '../../components/Tooltip'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'

interface FormInterface {
    newPassword: string
    confirmedPassword: string
}

const DEFAULT_INFO: FormInterface = {
    newPassword: '',
    confirmedPassword: '',
}

const NewPassword = () => {
    const [info, setInfo] = useState<FormInterface>(DEFAULT_INFO)
    const [errors, setErrors] = useState<FormInterface>(DEFAULT_INFO)
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const { email, code } = useRecoverPassword()
    const [show, setShow] = useState<{ password: boolean; confirmedPassword: boolean }>({
        password: false,
        confirmedPassword: false,
    })
    const [checkedRules, setCheckedRules] = useState<CheckRules>(DEFAULT_CHECKS)

    const handleCheckPassword = (password: string) => {
        setCheckedRules((prev) => ({
            ...prev,
            lowerCase: containsLowercase(password),
            upperCase: containsUppercase(password),
            number: containsNumber(password),
            specialCharacter: containsSpecialCharacter(password),
            long: password.length >= 8,
            notBlankSpace: containsNotBlankSpace(password),
        }))
    }

    const handleOnChangePassword = (event: ChangeEvent<HTMLInputElement>) => {
        handleCheckPassword(event.target.value as string)
        setInfo((prev) => ({ ...prev, newPassword: event.target.value }))
    }

    const handleOnChangeRepeatPassword = (event: ChangeEvent<HTMLInputElement>) => {
        setCheckedRules((prev) => ({
            ...prev,
            samePassword: event.target.value === info.newPassword,
        }))
        setInfo((prev) => ({ ...prev, confirmedPassword: event.target.value }))
    }

    const validateInfo = () => {
        const newErrors = {
            newPassword:
                info.newPassword === ''
                    ? 'Completá este campo.'
                    : validatePassword(info.newPassword)
                      ? ''
                      : 'La nueva contraseña no es válida.',
            confirmedPassword:
                info.confirmedPassword === ''
                    ? 'Completá este campo.'
                    : info.newPassword !== info.confirmedPassword
                      ? 'Las contraseñas deben coincidir.'
                      : '',
        }

        setErrors(newErrors)

        return Object.values(newErrors).every((x) => x === '')
    }

    const handleConfirmPassword = async () => {
        if (!validateInfo()) return

        const response = await new RecoverPasswordService().updatePassword(
            email,
            code,
            info.newPassword,
        )
        if (response.status === 200) {
            navigate(AppRoutes.login)
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }
    }

    return (
        <>
            <CustomPaper className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] md:w-[450px] py-6">
                <div className="flex items-center justify-center gap-3 md:gap-6 px-2 mb-4 md:mb-8 w-[85%]">
                    <img
                        src="/logo.png"
                        alt="logo"
                        className="h-[45px] md:h-[60px]"
                    />
                    <span className="text-primary font-semibold text-base md:text-xl">
                        Sistema de seguimiento de graduados
                    </span>
                </div>
                <span className="text-secondary text-base md:text-xl">Recuperar Contraseña</span>
                <Divider />
                <div className="flex flex-col gap-3 md:gap-6 mt-4 md:mt-8">
                    <CustomInput
                        type={show.password ? 'text' : 'password'}
                        label="Nueva Contraseña"
                        placeholder="Ingresá una nueva contraseña"
                        name="newPassword"
                        value={info.newPassword}
                        onChange={handleOnChangePassword}
                        errorText={errors.newPassword}
                        error={errors.newPassword !== ''}
                        className="tooltip"
                        iconButtons={[
                            {
                                icon: show.password ? <VisibilityIcon /> : <VisibilityOffIcon />,
                                action: () =>
                                    setShow((prev) => ({
                                        ...prev,
                                        password: !prev.password,
                                    })),
                            },
                        ]}
                    />
                    <CustomInput
                        type={show.confirmedPassword ? 'text' : 'password'}
                        label="Confirmar Contraseña"
                        placeholder="Confirmá la nueva contraseña"
                        name="confirmedPassword"
                        value={info.confirmedPassword}
                        onChange={handleOnChangeRepeatPassword}
                        errorText={errors.confirmedPassword}
                        error={errors.confirmedPassword !== ''}
                        className="tooltip"
                        iconButtons={[
                            {
                                icon: show.confirmedPassword ? (
                                    <VisibilityIcon />
                                ) : (
                                    <VisibilityOffIcon />
                                ),
                                action: () =>
                                    setShow((prev) => ({
                                        ...prev,
                                        confirmedPassword: !prev.confirmedPassword,
                                    })),
                            },
                        ]}
                    />
                </div>
                <CustomButton
                    text="Confirmar"
                    className="w-full mt-2 md:mt-4"
                    onClick={handleConfirmPassword}
                />
            </CustomPaper>
            <Tooltip
                anchorSelect=".tooltip"
                place="right"
                className="shadow-custom bg-secondary text-white rounded-md w-[260px]"
            >
                <PasswordTooltip checked={checkedRules} />
            </Tooltip>
        </>
    )
}

export default NewPassword
